import React, { useDeferredValue } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Receipt as ReceiptIcon,
  Payment as AddPaymentIcon,
  Assignment as LedgerIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import AccountBillsTable from "modules/bills/components/AccountBillsTable";
import AccountPaymentsTable from "modules/payments/components/AccountPaymentsTable";
import { DIALOGS } from "../hooks/useDialogManager";
import Gate from "components/Gate";
import { needsMessage, useCan } from "modules/settings/accessRoles";

const BillingPaymentsSection = React.memo(function BillingPaymentsSection({
  expanded,
  onToggle,
  openDialog,
}) {
  const account = useSelector((state) => state.account);
  const canEditBlocks = useCan()("paymentSetup");
  const bills = useSelector((state) => state.bills);
  const payments = useSelector((state) => state.payments);

  const deferredBills = useDeferredValue(bills);
  const deferredPayments = useDeferredValue(payments);

  const acct = account.data?.account;
  const timesLate = bills.accountBillingTimesLate ?? 0;
  const timesLateLoading = bills.accountBillingTimesLateIsLoading;

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      elevation={1}
      sx={{
        borderRadius: 2,
        "&:before": { display: "none" },
        boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: "grey.50",
          borderRadius: "8px 8px 0 0",
          "&.Mui-expanded": { borderRadius: "8px 8px 0 0" },
        }}
      >
        <Box display="flex" alignItems="center" gap={2} sx={{ width: "100%" }}>
          <Box display="flex" alignItems="center" gap={2} sx={{ flex: 1 }}>
            <ReceiptIcon color="primary" />
            <Typography variant="h5" fontWeight="600">
              Billing & Payments
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              {!deferredBills.isLoading && deferredBills.data && (
                <Chip
                  label={`${deferredBills.accountBillsTotalRows || 0} bills`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {!deferredPayments.isLoading && deferredPayments.data && (
                <Chip
                  label={`${deferredPayments.accountPaymentsTotalRows || 0} payments`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              )}
              {!timesLateLoading && timesLate > 0 && (
                <Chip
                  icon={<ScheduleIcon sx={{ fontSize: "0.9rem" }} />}
                  label={`Times Late: ${timesLate}`}
                  size="small"
                  color={timesLate >= 3 ? "warning" : "default"}
                  variant="outlined"
                />
              )}
              {Number(acct?.badCards) >= 1 && (
                <Tooltip title={canEditBlocks ? "Click to edit" : needsMessage("paymentSetup")} arrow>
                  <Chip
                    icon={<EditIcon sx={{ fontSize: "0.85rem" }} />}
                    label={`Bad Cards: ${acct.badCards}`}
                    size="small"
                    color="error"
                    variant={Number(acct.badCards) >= 3 ? "filled" : "outlined"}
                    onClick={canEditBlocks ? (e) => { e.stopPropagation(); openDialog(DIALOGS.PAYMENT_BLOCKS); } : undefined}
                    sx={{ cursor: canEditBlocks ? "pointer" : "default" }}
                  />
                </Tooltip>
              )}
              {Number(acct?.badChecks) >= 1 && (
                <Tooltip title={canEditBlocks ? "Click to edit" : needsMessage("paymentSetup")} arrow>
                  <Chip
                    icon={<EditIcon sx={{ fontSize: "0.85rem" }} />}
                    label={`Bad Checks: ${acct.badChecks}`}
                    size="small"
                    color="error"
                    variant={Number(acct.badChecks) >= 3 ? "filled" : "outlined"}
                    onClick={canEditBlocks ? (e) => { e.stopPropagation(); openDialog(DIALOGS.PAYMENT_BLOCKS); } : undefined}
                    sx={{ cursor: canEditBlocks ? "pointer" : "default" }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LedgerIcon />}
              onClick={(e) => {
                e.stopPropagation();
                openDialog(DIALOGS.LEDGER);
              }}
              sx={{
                minWidth: "auto",
                px: 2,
                py: 0.5,
                fontSize: "0.75rem",
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              View Ledger
            </Button>
            <Gate capability="paymentSetup">
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon sx={{ fontSize: "0.85rem" }} />}
              onClick={(e) => {
                e.stopPropagation();
                openDialog(DIALOGS.PAYMENT_BLOCKS);
              }}
              sx={{
                minWidth: "auto",
                px: 2,
                py: 0.5,
                fontSize: "0.75rem",
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Bad Cards & Checks
            </Button>
            </Gate>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            width: "100%",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 2,
              p: 2,
              backgroundColor: "grey.50",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                fontWeight="600"
                sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <ReceiptIcon color="primary" fontSize="small" />
                Bills & Billing History
              </Typography>
            </Box>
            <Box sx={{ width: "100%", overflow: "auto" }}>
              <AccountBillsTable />
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 2,
              p: 2,
              backgroundColor: "grey.50",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                fontWeight="600"
                sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <AddPaymentIcon color="secondary" fontSize="small" />
                Payments & Payment History
              </Typography>
            </Box>
            <Box sx={{ width: "100%", overflow: "auto" }}>
              <AccountPaymentsTable />
            </Box>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});

export default BillingPaymentsSection;
